import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getJobById, createJob, editJob } from "../utils/methods";
import { QuillEditor, InputComp } from "../components";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { strings } from "../utils/strings";
import Button from "../components/Button";
import useFetchOptions from "../hooks/useFetchOptions";
import { toast } from "react-toastify";
import Template from "../utils/templates";

const JobEdit = () => {
  const { id: currentId } = useParams();
  const navigate = useNavigate();
  const [jobData, setJobData] = useState({});
  const [title, setTitle] = useState("");
  const [industry, setIndustry] = useState("");
  const [role, setRole] = useState("");
  const [jobsType, setJobType] = useState("");
  const [jobOffices, setOffices] = useState("");
  const [expLevel, setExpLevel] = useState("");
  const [isDisabled, setIsDisabled] = useState(false);

  const {
    jobIndustry,
    jobExpLevel,
    jobRole,
    jobType,
    allJobOffices,
    loading,
    error,
  } = useFetchOptions();

  useEffect(() => {
    const loadJobData = async () => {
      if (currentId !== "new") {
        try {
          const job = await getJobById(currentId);
          setJobData(job);
          setTitle(job.title || "New Job");
          setIndustry(job.industry || "");
          setRole(job.role || "");
          setJobType(job.jobsType || "");
          setOffices(job.offices || "");
          setExpLevel(job.expLevel || "");
        } catch (error) {
          console.error("Error loading Job:", error);
        }
      }
    };
    loadJobData();
  }, [currentId]);

  const handleInputChange = (field, value) => {
    setJobData((prevData) => ({ ...prevData, [field]: value }));
  };

  const handleOptionChange = (key, newValue) => {
    setJobData((prevData) => ({
      ...prevData,
      [key]: newValue || "",
    }));

    // Update corresponding state if necessary
    switch (key) {
      case "industry":
        setIndustry(newValue || "");
        break;
      case "jobsType":
        setJobType(newValue || "");
        break;
      case "role":
        setRole(newValue || "");
        break;
      case "jobOffices":
        setOffices(newValue || "");
        break;
      case "expLevel":
        setExpLevel(newValue || "");
        break;
      default:
        break;
    }
  };

  const handleTitleChange = (key, newTitle) => {
    setTitle(newTitle);
    setJobData((prevData) => ({ ...prevData, [key]: newTitle }));
  };

  const handleSave = async () => {
    const fieldsToValidate = [
      { key: "title", value: title, label: strings["inputTitle"], regex: null },
      {
        key: "industry",
        value: industry,
        label: strings["industryCategory"],
        regex: null,
      },
      {
        key: "jobsType",
        value: jobType,
        label: strings["jobTypeCategory"],
        regex: null,
      },
      { key: "role", value: role, label: strings["roleCategory"], regex: null },
      {
        key: "jobOffices",
        value: jobOffices,
        label: strings["officeCategory"],
        regex: null,
      },
      {
        key: "expLevel",
        value: expLevel,
        label: strings["expLevelCategory"],
        regex: null,
      },
      { key: "desc", value: jobData.desc, label: "Description", regex: null },
    ];
    let isFormValid = true;

    fieldsToValidate.forEach(({ key, value, label, regex }) => {
      if (!value || (regex && !regex.test(value))) {
        isFormValid = false;
        toast.error(`${label} is required and must be valid.`);
      }
    });

    if (!isFormValid) return;

    try {
      if (currentId !== "new") {
        setIsDisabled(true);
        await editJob(currentId, { ...jobData, title });
      } else {
        setIsDisabled(true);
        await createJob({ ...jobData, title, status: "active" });
      }
      navigate(`/job`);
    } catch (error) {
      setIsDisabled(false);
      console.error("Error saving Job:", error);
    }
  };

  if (loading) return <p>Loading...</p>;
  if (error) return <p>Error loading options: {error.message}</p>;

  return (
    <div className="bg-border w-full h-full overflow-scroll px-[72px] pt-12 pb-[60px]">
      <p className="text-[36px] font-semibold">
        {currentId === "new"
          ? `${strings["addBlogTitle"]} ${jobData.title || "New"}`
          : `${strings["EditBlogTitle"]} ${jobData.title}`}
      </p>

      {/* Title */}
      <div className="flex space-x-[16px] w-[100%]">
        <div className="w-[50%]">
          <InputComp
            label={strings["inputTitle"]}
            placeHolder={strings["titlePlaceholder"]}
            type="text"
            value={title}
            handleChange={handleTitleChange}
            inputKey="title"
            labelClasses="text-sm font-medium pb-[6px] pt-5 text-color-1"
            classes="h-11 text-color-1 border-color-9 hover:border-black border-1 !w-[100%]"
          />
        </div>

        <div className="mt-5 w-[50%]">
          <div className="mb-[2px]">
            <label className="text-sm font-medium text-color-1">
              {strings["industryCategory"]}
            </label>
          </div>
          <Autocomplete
            className="!w-full"
            options={jobIndustry}
            getOptionLabel={(option) => option || ""}
            value={industry}
            onChange={(event, newValue) =>
              handleOptionChange("industry", newValue)
            }
            disableClearable
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: 44,
                    padding: 0,
                    paddingLeft: 1,
                    borderRadius: "8px",
                    backgroundColor: "white",
                    display: "flex",
                    alignItems: "center",
                  },
                  "& .MuiInputBase-input": {
                    height: "100%",
                    padding: "0 12px",
                    textAlign: "start",
                    boxSizing: "border-box",
                  },
                }}
              />
            )}
          />
        </div>
      </div>

      <div className="flex space-x-[16px] w-[100%]">
        <div className="mt-5 w-[50%]">
          <div className="mb-[2px]">
            <label className="text-sm font-medium text-color-1">
              {strings["jobTypeCategory"]}
            </label>
          </div>
          <Autocomplete
            className="!w-full"
            options={jobType}
            getOptionLabel={(option) => option || ""}
            value={jobsType}
            onChange={(event, newValue) =>
              handleOptionChange("jobsType", newValue)
            }
            disableClearable // Removes the close button
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: 44,
                    padding: 0,
                    paddingLeft: 1,
                    borderRadius: "8px",
                    backgroundColor: "white",
                    display: "flex",
                    alignItems: "center",
                  },
                  "& .MuiInputBase-input": {
                    height: "100%",
                    padding: "0 12px",
                    textAlign: "start",
                    boxSizing: "border-box",
                  },
                }}
              />
            )}
          />
        </div>

        <div className="mt-5 w-[50%]">
          <div className="mb-[2px]">
            <label className="text-sm font-medium text-color-1">
              {strings["roleCategory"]}
            </label>
          </div>
          <Autocomplete
            className="!w-full"
            options={jobRole || []}
            getOptionLabel={(option) => option || ""}
            value={role}
            onChange={(event, newValue) => handleOptionChange("role", newValue)}
            disableClearable // Removes the close button
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: 44,
                    padding: 0,
                    paddingLeft: 1,
                    borderRadius: "8px",
                    backgroundColor: "white",
                    display: "flex",
                    alignItems: "center",
                  },
                  "& .MuiInputBase-input": {
                    height: "100%",
                    padding: "0 12px",
                    textAlign: "start",
                    boxSizing: "border-box",
                  },
                }}
              />
            )}
          />
        </div>
      </div>

      <div className="flex space-x-[16px] w-[100%]">
        <div className="mt-5 w-[50%]">
          <div className="mb-[2px]">
            <label className="text-sm font-medium text-color-1">
              {strings["officeCategory"]}
            </label>
          </div>
          <Autocomplete
            className="!w-full"
            options={allJobOffices || []}
            getOptionLabel={(option) => option || ""}
            value={jobOffices}
            onChange={(event, newValue) =>
              handleOptionChange("jobOffices", newValue)
            }
            disableClearable // Removes the close button
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: 44,
                    padding: 0,
                    paddingLeft: 1,
                    borderRadius: "8px",
                    backgroundColor: "white",
                    display: "flex",
                    alignItems: "center",
                  },
                  "& .MuiInputBase-input": {
                    height: "100%",
                    padding: "0 12px",
                    textAlign: "start",
                    boxSizing: "border-box",
                  },
                }}
              />
            )}
          />
        </div>

        <div className="mt-5 w-[50%]">
          <div className="mb-[2px]">
            <label className="text-sm font-medium text-color-1">
              {strings["expLevelCategory"]}
            </label>
          </div>
          <Autocomplete
            className="!w-full"
            options={jobExpLevel}
            getOptionLabel={(option) => option || ""}
            value={expLevel}
            onChange={(event, newValue) =>
              handleOptionChange("expLevel", newValue)
            }
            disableClearable // Removes the close button
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: 44,
                    padding: 0,
                    paddingLeft: 1,
                    borderRadius: "8px",
                    backgroundColor: "white",
                    display: "flex",
                    alignItems: "center",
                  },
                  "& .MuiInputBase-input": {
                    height: "100%",
                    padding: "0 12px",
                    textAlign: "start",
                    boxSizing: "border-box",
                  },
                }}
              />
            )}
          />
        </div>
      </div>
      <div className="my-6 bg-white">
        <QuillEditor
          value={jobData.desc || Template}
          onChange={(content) => handleInputChange("desc", content)}
        />
      </div>

      {/* Upload Button */}

      <div className="flex justify-center w-full">
        <Button
          classes={`!w-full justify-center ${
            isDisabled ? "opacity-50 cursor-not-allowed" : ""
          }`}
          onClick={isDisabled ? null : handleSave}
          disabled={isDisabled}
        >
          {strings["submitButton"]}
        </Button>
      </div>
    </div>
  );
};

export default JobEdit;
